import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { takeUntil } from 'rxjs/operators';
import * as momentTz from 'moment-timezone';
import {
  AccountService,
  AlertService,
  CampaignProductService,
  CampaignService,
  CampaignTrackingService,
  CancelReasonCategoryService,
  CRMCampaignService,
  CustomerJourneyReportService,
  FunnelService,
  FunnelStepService,
  GuiService,
  MerchantService,
  ReportsConfigService,
  StorageService,
  UserService,
} from '../_services';
import { ReportSessionInput } from '../_models';
import { expandCollapse } from '../_shared';
import { FilterType, MultiOption, MultiOptions, MultiSubOption } from '../custom-multi-select/multi-select';
import { ReportsAbstractV3Component } from './reports-abstract-v3.component';
import {
  customerJourneyColumns,
  customerJourneyIntentByType,
  customerJourneyDimensionGroupPrefix,
  customerJourneyRowDimensionGroups,
  customerJourneyRowDimensionKeys,
  reportsFieldDataV3,
} from './utils';
import {
  CustomerJourney,
  CustomerJourneyBoolFilter,
  CustomerJourneyCell,
  CustomerJourneyDimensionGroup,
  CustomerJourneyFilter,
  CustomerJourneyFilterType,
  CustomerJourneyIntentType,
  CustomerJourneyOptionsResponse,
  CustomerJourneyQuery,
  CustomerJourneyType,
  CustomerJourneyTypeLabels,
  ReportDimensions,
} from './models/reports';

@Component({
  moduleId: module.id.toString(),
  selector: 'customer-journey-report-v3',
  templateUrl: './templates/reports-v3.component.html',
  styleUrls: ['./styles/reports-v2.component.scss', './styles/reports-v3.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [expandCollapse],
})
export class CustomerJourneyReportV3Component extends ReportsAbstractV3Component implements OnInit {
  fields: MultiOptions = [];
  filters: MultiOptions = [];
  selectedDimensions: { model: string; label: string; selected: boolean; disabled: boolean }[] = [];
  predefinedFields: string[] = [];
  commonReportsFields: string[] = [];
  summaryFields: string[] = [];
  showExcludeUpsellsFilter: boolean = false;
  showExcludeValidationsFilter: boolean = false;
  fetchesFilterOptionsOnInit: boolean = false;
  dropdownsLoading: boolean = false;
  showDateRange: boolean = false;
  showTemplateMenu: boolean = false;
  showRequiredMarkers: boolean = false;
  showPercentageToggle: boolean = false;
  downloadMenuLabel: string = 'Download CSV';
  showOverviewPanel: boolean = true;
  // Stays true until journey_options answers; the Details table waits on it.
  isCustomReportLoading: boolean = true;
  isOverviewLoading: boolean = true;

  journeyId: number = null;
  journeyType: CustomerJourneyType = CustomerJourneyType.Order;
  journey: CustomerJourney = null;
  // Filters whose values are the journey's own items, filled once journey_detail answers.
  private journeyItemFilterKeys: string[] = [];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected alertService: AlertService,
    public reportsService: CustomerJourneyReportService,
    protected campaignService: CampaignService,
    protected reportsConfigService: ReportsConfigService,
    protected trackingSourceService: CampaignTrackingService,
    protected userService: UserService,
    protected accountService: AccountService,
    protected campaignProductService: CampaignProductService,
    protected crmCampaignService: CRMCampaignService,
    protected storageService: StorageService,
    protected stepService: FunnelStepService,
    protected merchantService: MerchantService,
    protected modalService: NgxSmartModalService,
    protected cancelReasonCategoryService: CancelReasonCategoryService,
    protected funnelService: FunnelService,
    protected guiService: GuiService
  ) {
    super(
      router,
      location,
      route,
      alertService,
      reportsService,
      campaignService,
      reportsConfigService,
      trackingSourceService,
      userService,
      accountService,
      campaignProductService,
      crmCampaignService,
      storageService,
      stepService,
      merchantService,
      modalService,
      cancelReasonCategoryService,
      funnelService,
      guiService
    );
    this.fieldData = reportsFieldDataV3;
    this.bannerTitle = 'Customer Journey Report';
    this.bannerSubtitle =
      'Everything that happened on one order, subscription or account: every session, every intent and its outcome.';
  }

  ngOnInit() {
    const params = this.route.snapshot.params;
    if (params['journeyId'] && !isNaN(Number(params['journeyId']))) {
      this.journeyId = Number(params['journeyId']);
    }
    if (params['journeyType'] === CustomerJourneyType.Subscription) {
      this.journeyType = CustomerJourneyType.Subscription;
    } else if (params['journeyType'] === CustomerJourneyType.Return) {
      this.journeyType = CustomerJourneyType.Return;
    }

    super.ngOnInit();
    this.buildJourneyStrip();

    this.reportsService.journey$.pipe(takeUntil(this._destroy$)).subscribe((journey: CustomerJourney) => {
      this.setJourney(journey);
    });

    if (this.journeyId) {
      this.loadJourneyOptions();
    } else {
      this.isCustomReportLoading = false;
      this.isOverviewLoading = false;
      this.alertService.error('Journey ID is missing. Open this report from a Journey ID in a Retention report.');
    }
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.selectedDimensions, event.previousIndex, event.currentIndex);
  }

  // The journey has no date range, dashboard nav, saved templates or retention filter options.
  protected initFromDashboardNav(_dim: string) {}
  getDateDependentFiltersData() {}
  fetchAvailableReports() {}
  getSummary() {}

  getQueryForReport(): any {
    return this.buildJourneyQuery();
  }

  getQueryForSummary(): any {
    return this.buildJourneyQuery();
  }

  // Bound to the Details table at runtime, so it only reads the table's fieldData and storageService.
  getFieldValue(row: ReportSessionInput, field: string) {
    let value = row ? row[field] : null;
    const entry = this.fieldData[field];
    if (entry && entry.getter) {
      const timezone = this.storageService.get('timezone') || momentTz.tz.guess();
      value = entry.getter(row, value, timezone);
    } else if (value === null || value === undefined || value === '') {
      value = '--';
    }
    return value;
  }

  updateReportFlag() {
    super.updateReportFlag();
    this.isOverviewLoading = false;
  }

  get canRunReport(): boolean {
    return !this.isCustomReportLoading && !!this.journeyId;
  }

  get showDetailsTable(): boolean {
    return !this.isCustomReportLoading && !!this.journeyId;
  }

  get journeyIntent(): CustomerJourneyIntentType {
    return customerJourneyIntentByType[this.journeyType];
  }

  // The API only has order and subscription journeys; a return lives on its order journey.
  get apiJourneyType(): CustomerJourneyType {
    let result = this.journeyType;
    if (result === CustomerJourneyType.Return) {
      result = CustomerJourneyType.Order;
    }
    return result;
  }

  private buildJourneyQuery(): CustomerJourneyQuery {
    const dimensions = this.selectedDimensions.map((dimension) => String(dimension.model));
    this.selectedDimensionsList = customerJourneyColumns.concat(dimensions);
    const fields = dimensions.filter((dimension) => !customerJourneyRowDimensionKeys.includes(dimension));

    const filters = this.buildSelectedFiltersMap();
    this.topBarToggles.forEach((filter) => {
      if (filter.checked) {
        filters[filter.key] = true;
      }
    });

    return {
      journey_id: this.journeyId,
      journey_type: this.apiJourneyType,
      accounts: [this.queryParams.account_id],
      fields,
      filters,
      ordering: this.ordering,
    };
  }

  private loadJourneyOptions() {
    this.reportsService
      .getJourneyOptions()
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data: CustomerJourneyOptionsResponse) => {
          this.setDimensionOptions(this.mergeRowDimensions(data && data.groups ? data.groups : []));
          this.setFilterOptions(data && data.filters ? data.filters : []);
          this.reportReq = this.getQueryForReport();
          this.isCustomReportLoading = false;
        },
        (error) => {
          // The fixed Details columns still load; the row dimensions stay pickable.
          this.setDimensionOptions(this.mergeRowDimensions([]));
          this.reportReq = this.getQueryForReport();
          this.isCustomReportLoading = false;
          this.handleError(error);
        }
      );
  }

  // Puts the row dimensions at the top of their journey_options group, adding the group when the API has none.
  private mergeRowDimensions(groups: CustomerJourneyDimensionGroup[]): CustomerJourneyDimensionGroup[] {
    const merged: CustomerJourneyDimensionGroup[] = groups.map((group) => ({
      ...group,
      dimensions: [...group.dimensions],
    }));
    customerJourneyRowDimensionGroups.forEach((rowGroup) => {
      const group = merged.find((item) => item.key === rowGroup.key);
      if (group) {
        const extra = rowGroup.dimensions.filter(
          (row) => !group.dimensions.some((dimension) => dimension.key === row.key)
        );
        group.dimensions = extra.concat(group.dimensions);
      } else {
        merged.push({ ...rowGroup, dimensions: [...rowGroup.dimensions] });
      }
    });
    return merged;
  }

  private setDimensionOptions(groups: CustomerJourneyDimensionGroup[]) {
    // outcome is shared with the Resolve report, whose header is 'Lookup Outcome'.
    const labels: { [key: string]: string } = { [ReportDimensions.Outcome]: 'Outcome' };
    const defaults: MultiSubOption[] = [];

    this.fields = groups.map((group) => {
      const options: MultiSubOption[] = group.dimensions.map((dimension) => {
        const option: MultiSubOption = {
          model: dimension.key,
          label: dimension.label,
          selected: dimension.default,
          disabled: false,
        };
        labels[dimension.key] = dimension.label;
        if (dimension.default) {
          defaults.push(option);
        }
        return option;
      });

      return {
        model: customerJourneyDimensionGroupPrefix + group.key,
        label: group.label,
        selected: false,
        disabled: false,
        activeCount: options.filter((option) => option.selected).length,
        searchTerm: '',
        type: FilterType.Dropdown,
        options,
      };
    });

    this.fieldLabels = labels;
    this.selectedDimensions = defaults as { model: string; label: string; selected: boolean; disabled: boolean }[];
  }

  private setFilterOptions(filters: CustomerJourneyFilter[]) {
    const pickerFilters: MultiOptions = [];
    const selectedFilters: MultiOptions = [];
    const boolFilters: CustomerJourneyBoolFilter[] = [];

    filters.forEach((filter) => {
      if (filter.type === CustomerJourneyFilterType.Bool) {
        boolFilters.push({ key: filter.key, label: filter.label, checked: false });
      } else {
        const isJourneyItems = filter.type === CustomerJourneyFilterType.JourneyItems;
        if (isJourneyItems) {
          this.journeyItemFilterKeys.push(filter.key);
        }
        // Intent Type starts on the intent of the report the Journey ID was clicked in.
        const isIntentType = filter.key === ReportDimensions.IntentType;
        const options = (filter.values || []).map((value) => ({
          model: value.value,
          label: value.label,
          selected: isIntentType && value.value === this.journeyIntent,
          disabled: false,
        }));
        const pickerFilter: MultiOption = {
          model: filter.key,
          label: filter.label,
          selected: false,
          disabled: false,
          loading: isJourneyItems && !this.journey,
          activeCount: options.filter((option) => option.selected).length,
          searchTerm: '',
          type: FilterType.Dropdown,
          options,
        };
        pickerFilters.push(pickerFilter);
        if (pickerFilter.activeCount > 0) {
          selectedFilters.push(pickerFilter);
        }
      }
    });

    this.filters = pickerFilters;
    this.selectedFilters = selectedFilters;
    this.topBarToggles = boolFilters;
    this.setJourneyItemFilterOptions();
  }

  private setJourney(journey: CustomerJourney) {
    this.journey = journey;
    this.buildJourneyStrip();
    this.buildJourneyOverview();
    this.setJourneyItemFilterOptions();
  }

  // Item values come from journey_detail; set them once so a selection survives paging and sorting.
  private setJourneyItemFilterOptions() {
    if (this.journey && this.journey.item_options) {
      this.journeyItemFilterKeys.forEach((key) => {
        const filter: MultiOption = this.findFilter(key);
        if (filter && (!filter.options || !filter.options.length)) {
          filter.options = this.journey.item_options.map((item) => ({
            model: item.id,
            label: item.name ? item.name : String(item.id),
            selected: false,
            disabled: false,
          }));
          filter.loading = false;
        }
      });
    }
  }

  private buildJourneyStrip() {
    const journey = this.journey;
    const journeyType = journey && journey.journey_type ? journey.journey_type : this.apiJourneyType;

    const strip: CustomerJourneyCell[] = [{ label: 'Journey ID', value: this.displayValue(this.journeyId) }];
    if (journeyType !== CustomerJourneyType.Subscription) {
      strip.push({ label: 'Order ID', value: this.displayValue(journey ? journey.order_id : null) });
    }
    strip.push({ label: 'Journey Type', value: this.displayValue(CustomerJourneyTypeLabels[journeyType]) });
    strip.push({ label: 'Customer ID', value: this.displayValue(journey ? journey.customer_id : null) });
    strip.push({ label: 'Brand', value: this.displayValue(journey ? journey.brand : null) });

    this.topBarFields = strip;
  }

  // Nine cards, which the shared Overview lays out four per row: outcome row, counts row, then Journey Span.
  private buildJourneyOverview() {
    let cells: CustomerJourneyCell[] = [];
    const journey = this.journey;

    if (journey) {
      cells = [
        { label: 'Status', value: this.displayValue(journey.status) },
        { label: 'Primary Intent', value: this.displayValue(journey.primary_intent) },
        { label: 'Journey Outcome', value: this.getJourneyOutcome(journey) },
        { label: 'Return Outcome', value: this.displayValue(journey.return_outcome) },
        { label: 'Items', value: this.displayValue(journey.items) },
        { label: 'Sessions', value: this.displayValue(journey.session_count) },
        { label: 'Intents', value: this.displayValue(journey.intent_count) },
        { label: 'Support Contacts', value: this.displayValue(journey.support_contacts) },
        { label: 'Journey Span', value: this.getJourneySpan(journey.span_days) },
      ];
    }

    this.setOverviewCards(
      cells.map((cell) => ({ key: cell.label, label: cell.label, value: null, display: cell.value }))
    );
  }

  private getJourneyOutcome(journey: CustomerJourney): string {
    let result = this.displayValue(journey.journey_outcome);
    const hasItemCounts =
      journey.items_kept !== null &&
      journey.items_kept !== undefined &&
      journey.items_total !== null &&
      journey.items_total !== undefined;
    if (
      journey.journey_outcome &&
      journey.primary_intent_type === CustomerJourneyIntentType.Retention &&
      hasItemCounts
    ) {
      result = `${journey.journey_outcome} · ${journey.items_kept} of ${journey.items_total} items kept`;
    }
    return result;
  }

  private getJourneySpan(spanDays: number | null): string {
    let result = '--';
    if (spanDays !== null && spanDays !== undefined) {
      result = spanDays === 1 ? '1 day' : spanDays + ' days';
    }
    return result;
  }

  private displayValue(value: string | number | null): string {
    let result = '--';
    if (value !== null && value !== undefined && value !== '') {
      result = String(value);
    }
    return result;
  }
}
